<template>
  <div class="c-clients">
    <v-container fluid>
      <v-row>
        <v-col cols="12">
          <v-card
            class="projects-card"
          >
            <v-card-title>
              <div class="d-flex">
                <v-text-field
                  v-model="search"
                  class="mr-5"
                  append-icon="mdi-magnify"
                  label="Search"
                  hide-details
                  dense
                ></v-text-field>
                <v-select
                  dense
                  hide-details
                  label="Statuses"
                  v-model="filterByStatus"
                  :items="projectStatuses"
                  multiple
                  item-text="Description"
                  item-value="Status_ID"
                  clearable
                >
                <template v-slot:prepend-item>
                  <v-list-item
                    ripple
                    @mousedown.prevent
                    @click="toggle"
                  >
                    <v-list-item-action>
                      <v-icon :color="filterByStatus.length > 0 ? 'primary' : ''">
                        {{ icon }}
                      </v-icon>
                    </v-list-item-action>
                    <v-list-item-content>
                      <v-list-item-title>
                        Select All
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                  <v-divider class="mt-2"></v-divider>
                </template>
                </v-select>
              </div>
              <v-spacer></v-spacer>
              <v-btn
                v-if="canAdd"
                color="success"
                small
                @click.prevent="createNewProject"
              >
                Add Project
              </v-btn>
            </v-card-title>
            <v-divider></v-divider>
            <v-card-text class="projects-grid-container" ref="gridContainer">
              <v-data-table
                :height="tableHeight"
                class="projects-grid"
                dense
                fixed-header
                mobile-breakpoint="0"
                :search="search"
                :headers="headers"
                :item-class="itemClass"
                item-key="Project_ID"
                :items="projects"
                @click:row="onRowClick"
                :items-per-page="-1"
              >
                <template v-slot:[`item.Start_Date`]="{ value }">
                  {{ value | dateFilter }}
                </template>
              </v-data-table>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
    <ProjectDetails
      v-model="detailsData"
      @saveProject="onProjectSave"
      @updateList="getProjects"
      @projectUpdated="onProjectUpdate"
      :key="`details-key-${detailsDialogKey}`"
    />
  </div>
</template>
<script>
export default {
  name: 'Projects',
  components: {
    ProjectDetails: () => import('@/components/projects/ProjectDetails')
  },
  data () {
    return {
      search: '',
      showDetails: false,
      selectedProject: null,
      detailsDialogKey: 0,
      filterByStatus: ['S'],
      rolesOfCurrentUser: [],
      tableHeight: 0
    }
  },
  computed: {
    selectAllStatuses () {
      return this.filterByStatus.length === this.projectStatuses.length
    },
    selectSomeStatuses () {
      return this.filterByStatus.length > 0 && !this.selectAllStatuses
    },
    icon () {
      if (this.selectAllStatuses) return 'mdi-close-box'
      if (this.selectSomeStatuses) return 'mdi-minus-box'
      return 'mdi-checkbox-blank-outline'
    },
    projectManagementHistory () {
      return this.$store.getters.getProjectManagementHistory
    },
    detailsData: {
      get () {
        return {
          show: this.showDetails,
          project: this.selectedProject
        }
      },
      set (val) {
        this.showDetails = val.show
        this.selectedProject = val.project
      }
    },
    headers () {
      return [
        {
          text: 'Name',
          value: 'Description',
          divider: true,
          class: 'px-2'
        },
        {
          text: 'Client',
          value: 'ClientName',
          divider: true,
          class: 'px-2'
        },
        {
          text: 'Project Group',
          value: 'Project_Group',
          divider: true,
          class: 'px-2'
        },
        {
          text: 'Status',
          value: 'StatusDescription',
          divider: true,
          class: 'px-2'
        },
        {
          text: 'Department',
          value: 'Department',
          divider: true,
          class: 'px-2'
        },
        {
          text: 'Account Manager',
          value: 'AMFullName',
          divider: true,
          class: 'px-2',
          cellClass: 'text-no-wrap'
        },
        {
          text: 'Project Manager',
          value: 'SBUFullName',
          divider: true,
          class: 'px-2',
          cellClass: 'text-no-wrap'
        },
        {
          text: 'Project Administrator',
          value: 'PAFullName',
          divider: true,
          class: 'px-2',
          cellClass: 'text-no-wrap'
        },
        {
          text: 'Starting Date',
          value: 'Start_Date',
          divider: true,
          class: 'px-2',
          cellClass: 'text-no-wrap'
        },
        {
          text: 'Integrated With',
          value: 'Integrated_With',
          class: 'px-2'
        }
      ]
    },
    user () {
      return this.$store.getters.getUser
    },
    projects () {
      let projects = this.$store.getters.getProjects
      let showIsDeleted = false
      if (this.filterByStatus && this.filterByStatus.length) {
        showIsDeleted = this.filterByStatus.includes('D')
        if (this.filterByStatus.length === 1 && showIsDeleted) {
          projects = projects.filter(item => item.Is_Deleted)
        } else {
          if (showIsDeleted) {
            projects = projects.filter(item => this.filterByStatus.includes(item.Status))
          } else {
            projects = projects.filter(item => this.filterByStatus.includes(item.Status)).filter(item => !item.Is_Deleted)
          }
        }
      }
      return projects
    },
    canAdd () {
      let res = false
      for (let i = 0; i < this.rolesOfCurrentUser.length; i++) {
        if (['manager', 'financier', 'hr'].indexOf(this.rolesOfCurrentUser[i].RoleName.toLowerCase()) > -1) {
          res = true
          break
        }
      }
      return res
    },
    projectStatuses () {
      return this.$store.getters.getProjectStatuses
    }
  },
  async mounted () {
    this.tableHeight = this.$refs.gridContainer.clientHeight - 80 + 'px'
    await this.getRolesOfCurrentUser()
    await this.getProjects()
    await this.getProjectStatuses()
    this.showProjectInfo()
  },
  methods: {
    toggle () {
      this.$nextTick(() => {
        if (this.selectAllStatuses) {
          this.filterByStatus = []
        } else {
          this.filterByStatus = this.projectStatuses.slice().map(status => status.Status_ID)
        }
      })
    },
    onProjectUpdate (e) {
      this.axios.get(`/api/project_by_id/${e}`, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(response => {
        const allProjects = [...this.projects]
        const projIndex = allProjects.findIndex(p => p.Project_ID === e)
        let integratedWith = null
        if (response.data[0].Integrated_With_Jira) integratedWith = 'Jira'
        if (response.data[0].Integrated_With_Redmine) integratedWith = 'Redmine'
        if (projIndex > -1) {
          allProjects.splice(projIndex, 1, {
            ...response.data[0],
            Integrated_With: integratedWith
          })
          this.$store.dispatch('setProjects', { projects: allProjects })
          const openedProjectID = this.detailsData && this.detailsData.project ? this.detailsData.project.Project_ID : null
          if (openedProjectID === e) {
            const state = this.$store.getters.getProjectDetailsDialogState
            this.detailsData = {
              show: true,
              project: {
                ...response.data[0],
                Get_Date: state && state.Project_ID === e ? state.Get_Date : response.data[0].Get_Date ? new Date(response.data[0].Get_Date).toISOString().substring(0, 10) : null,
                Start_Date: response.data[0].Start_Date ? new Date(response.data[0].Start_Date).toISOString().substring(0, 10) : null,
                Release_Date: state && state.Project_ID === response.data[0].Project_ID ? state.Release_Date : response.data[0].Release_Date ? new Date(response.data[0].Release_Date).toISOString().substring(0, 10) : null,
                Integrated_With: integratedWith,
                Description: state && state.Project_ID === e ? state.Description : response.data[0].Description,
                Project_Group: state && state.Project_ID === e ? state.Project_Group : response.data[0].Project_Group,
                Client_ID: state && state.Project_ID === e ? state.Client_ID : response.data[0].Client_ID,
                Dep_ID: state && state.Project_ID === e ? state.Dep_ID : response.data[0].Dep_ID,
                Keywords: state && state.Project_ID === e ? state.Keywords : response.data[0].Keywords,
                Status: state && state.Project_ID === e ? state.Status : response.data[0].Status,
                NDA: state && state.Project_ID === e ? state.NDA : response.data[0].NDA,
                Internal_Budget: state && state.Project_ID === e ? state.Internal_Budget : response.data[0].Internal_Budget,
                External_Budget: state && state.Project_ID === e ? state.External_Budget : response.data[0].External_Budget
              }
            }
          }
        }
      }).catch(error => {
        this.$store.dispatch('setError', { errorMsg: error.message + ' - ' + error.response.data })
      })
    },
    itemClass (item) {
      return item.Is_Deleted ? 'red lighten-5' : ''
    },
    async getRolesOfCurrentUser () {
      await this.axios.get('/api/employee_role/' + this.user.login, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(result => {
        this.rolesOfCurrentUser = result.data
      }).catch(error => {
        this.$store.dispatch('setError', { errorMsg: error.message + ' - ' + error.response.data })
      })
    },
    onRowClick (e) {
      this.showDetails = true
      e.Get_Date = e.Get_Date ? new Date(e.Get_Date).toISOString().substring(0, 10) : null
      e.Start_Date = e.Start_Date ? new Date(e.Start_Date).toISOString().substring(0, 10) : null
      e.Release_Date = e.Release_Date ? new Date(e.Release_Date).toISOString().substring(0, 10) : null
      this.selectedProject = e
      this.detailsDialogKey++
    },
    goTo () {
      this.$vuetify.goTo(
        '.data-table tbody tr',
        {
          offset: 50,
          container: '.v-data-table__wrapper'
        }
      )
    },
    async getProjectStatuses () {
      await this.axios.get('/api/project_statuses', {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(response => {
        const statuses = [...response.data, { Description: 'Deleted', Status_ID: 'D' }]
        this.$store.dispatch('setProjectStatuses', { projectStatuses: statuses })
      }).catch(error => {
        this.$store.dispatch('setError', { errorMsg: error.message + ' - ' + error.response.data })
      })
    },
    async getProjects () {
      await this.axios.get('/api/projects', {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(response => {
        const proj = []
        for (let i = 0; i < response.data.length; i++) {
          let integratedWith = ''
          if (response.data[i].Integrated_With_Jira) integratedWith = 'Jira'
          if (response.data[i].Integrated_With_Redmine) integratedWith = 'Redmine'
          proj.push({
            ...response.data[i],
            Integrated_With: integratedWith
          })
        }
        this.$store.dispatch('setProjects', { projects: proj })
        this.showProjectInfo()
      }).catch(error => {
        this.$store.dispatch('setError', { errorMsg: error.message + ' - ' + error.response.data })
      })
    },
    showProjectInfo () {
      if (this.$route.params.Project_ID) {
        const _project = this.$store.getters.getProjects.filter(item => item.Project_ID === this.$route.params.Project_ID)[0]
        if (_project) {
          this.onRowClick(_project)
          if (_project.Is_Deleted) {
            this.showDeleted = true
          }
        }
      }
      if (this.$route.params.Project_Name) {
        this.search = this.$route.params.Project_Name
      }
    },
    createNewProject () {
      this.onRowClick({
        Project_ID: null,
        Description: '',
        Client_ID: null,
        Status: 'S',
        Get_Date: new Date(),
        Start_Date: null,
        Release_Date: null,
        Show_Date: true,
        Show_Dev: true,
        Show_Descr: true,
        Show_Dur: true,
        Show_TotalDur: true,
        Show_For: true,
        Show_By: true,
        Show_PrepBy: true,
        Show_Summary: true,
        File_Mask: null,
        Report_Path: null,
        Show_Duration_Summary: true,
        Show_Task: true,
        Is_Deleted: false,
        Deleted_Notes: null,
        StatusReport_Subject: null,
        TT_ID: null,
        PreDefine_Task: null,
        Project_Group: '',
        NDA: false,
        Keywords: null,
        Marketing_Description: null,
        Quality_of_Development: 0,
        Quality_of_Document: 0,
        Code_Review: 0,
        Quality_of_Estimate: 0,
        Quality_of_Communication: 0,
        Client_Satisfaction: 0,
        Profitability_of_Project: 0,
        Quality_of_Design: 0,
        Allow_Publish: false,
        Icon: null,
        Icon_Filename: null,
        Private_Info: null,
        Internal_Budget: 0,
        External_Budget: 0,
        Quality_Notes: null,
        Account_Manager_ID: null,
        Project_Administrator_ID: null,
        Project_Manager_ID: null,
        SBU_ID: null,
        ClientName: '',
        StatusDescription: 'Started',
        AMFullName: null,
        PAFullName: null,
        PMFullName: null,
        SBUFullName: null,
        Dep_ID: null,
        Department: ''
      })
    },
    onProjectSave (e) {
      this.$store.dispatch('setProjectDetailsDialogState', { state: null })
      if (e.Project_ID) {
        this.updateProject(e)
      } else {
        this.createProject(e)
      }
    },
    async updateProject (e) {
      await this.axios.put(`/api/projects/${e.Project_ID}`, e, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(async (response) => {
        if (e.Is_Deleted) {
          await this.deleteAllIntegrationsFromProject(e.Project_ID)
        }
        this.onProjectUpdate(e.Project_ID)
        this.$store.dispatch('setSuccess', { successMsg: 'Project successfully updated' })
      }).catch(error => {
        this.$store.dispatch('setError', { errorMsg: error.message + ' - ' + error.response.data })
      })
    },
    createProject (e) {
      this.axios.post('/api/projects', e, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(response => {
        e.Project_ID = response.data[0].Project_ID
        this.$store.dispatch('addProject', { project: e })
        this.selectedProject = e
        this.$store.dispatch('setSuccess', { successMsg: 'Project successfully created' })
      }).catch(error => {
        this.$store.dispatch('setError', { errorMsg: error.message + ' - ' + error.response.data })
      })
    },
    async deleteAllIntegrationsFromProject (pId) {
      await this.axios.delete(`/api/integrations/delete-all-integrations/${pId}`, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(() => {
        this.$store.dispatch('setSuccess', { successMsg: 'Integrations were deleted from project' })
      }).catch(error => {
        this.$store.dispatch('setError', { errorMsg: error.message + ' - ' + error.response.data })
      })
    }
  },
  filters: {
    dateFilter (val) {
      if (val) return new Date(val).toISOString().substring(0, 10)
      return null
    }
  }
}
</script>
<style lang="scss" scoped>
  .c-clients {
    height: 100%;
  }
  .projects-card {
    height: calc(100vh - 80px);
    display: flex;
    flex-direction: column;
  }
  .projects-grid-container {
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    height: calc(100% - 62px);
    max-height: calc(100% - 62px);
    .projects-grid {
      width: 100%;
      flex-grow: 1;
      display: flex;
      flex-direction: column;
    }
  }
  ::v-deep .v-data-table__wrapper {
    flex-grow: 1;
  }
</style>
